import React, { useState, useEffect } from "react";
import {
  Wallet,
  Database,
  ChefHat,
  GraduationCap,
  Baby,
  CreditCard,
  Building2,
  Users,
  ShieldCheck,
  ArrowDown,
  Receipt,
  Repeat,
  Coins,
  Banknote,
  CalendarCheck,
  CheckCircle2,
  RefreshCw,
  Activity,
  Network,
} from "lucide-react";
import { I18N, V_COLORS, MODEL_META } from "./ecolunch_i18n";
import { FLOWS, FLOWS_EN_LABELS } from "./ecolunch_flows";
import FlowDiagram from "./FlowDiagram";
import MegaMasterDiagram from "./MegaMasterDiagram";
import "./EcoLunchDeck.css";

/* ---- Tab icons (Lucide only — NO emojis) ---- */
const TAB_ICONS = {
  resto: ChefHat,
  kiddo: Baby,
  school: GraduationCap,
  fin: CreditCard,
};

/* ============================================================
   TOP NAV
   ============================================================ */
const TopNav = ({ lang, setLang, t }) => {
  const links = [
    { id: "hero", label: t.nav.hero },
    { id: "roadmap", label: t.nav.roadmap },
    { id: "mega-flow", label: t.megaFlow.navLabel },
    { id: "verticals", label: t.nav.verticals },
    { id: "wallet", label: t.nav.wallet },
    { id: "ledger", label: t.nav.ledger },
    { id: "why", label: t.nav.why },
  ];
  return (
    <nav className="ed-nav" data-testid="ed-nav">
      <div className="ed-nav-inner">
        <a href="#hero" className="ed-nav-brand" data-testid="nav-brand">
          <img src="/ecolunch-logo-white.png" alt="EcoLunch" className="ed-nav-logo" />
        </a>
        <div className="ed-nav-links">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              className="ed-nav-link"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </a>
          ))}
        </div>
        <div className="ed-lang-toggle" data-testid="lang-toggle">
          <button
            type="button"
            className={`ed-lang-btn ${lang === "fr" ? "active" : ""}`}
            onClick={() => setLang("fr")}
            data-testid="lang-fr"
          >
            FR
          </button>
          <button
            type="button"
            className={`ed-lang-btn ${lang === "en" ? "active" : ""}`}
            onClick={() => setLang("en")}
            data-testid="lang-en"
          >
            EN
          </button>
          <span
            className="ed-lang-pill"
            style={{ transform: `translateX(${lang === "en" ? "100%" : "0%"})` }}
          />
        </div>
      </div>
    </nav>
  );
};

/* ============================================================
   HERO
   ============================================================ */
const Hero = ({ t }) => (
  <section id="hero" className="ed-hero" data-testid="ed-hero">
    <div className="ed-hero-inner">
      <div className="ed-hero-mor-badge" data-testid="hero-mor-badge">
        <ShieldCheck size={14} strokeWidth={2.4} />
        <span>EcotechSystem inc. · <strong>Sole Merchant of Record · MID Holder</strong></span>
      </div>
      <img src="/ecolunch-fox.png" alt="EcoLunch" className="ed-hero-logo ed-hero-logo-fox" />
      <h1 className="ed-hero-title">
        {t.hero.title.split("\n").map((line, i) => (
          <span key={i} className="ed-hero-title-line">{line}</span>
        ))}
      </h1>
      <p className="ed-hero-subtitle">{t.hero.subtitle}</p>
      <div className="ed-hero-pills">
        {t.hero.pills.map((p, i) => (
          <span key={i} className="ed-hero-pill" style={{ animationDelay: `${i * 80}ms` }}>
            {p}
          </span>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================
   SECTION HEADER
   ============================================================ */
const SectionHeader = ({ title, subtitle, color }) => (
  <header className="ed-section-head">
    <div className="ed-section-bar" style={{ background: color }} />
    <h2 className="ed-section-title">{title}</h2>
    {subtitle && <p className="ed-section-subtitle">{subtitle}</p>}
  </header>
);

/* ============================================================
   COMMON RULE BANNER (between Hero and Roadmap)
   ============================================================ */
/* ============================================================
   PAYOUT CYCLE — many-to-one aggregation block
   ============================================================ */
const PayoutCycle = ({ t }) => {
  const p = t.payoutCycle;
  return (
    <section id="payout-cycle" className="ed-section ed-payout-cycle" data-testid="ed-payout-cycle">
      <SectionHeader title={p.title} subtitle={p.subtitle} color="#f472b6" />
      <div className="ed-pc-tag">{p.tag}</div>
      <div className="ed-pc-card">
        <div className="ed-pc-card-head">
          <span className="ed-pc-payee">{p.payee}</span>
          <span className="ed-pc-cycle">{p.cycle}</span>
        </div>
        <ul className="ed-pc-entries">
          {p.entries.map((e) => (
            <li key={e.id} className={`ed-pc-entry ed-pc-entry-${e.kind}`} data-testid={`pc-entry-${e.id}`}>
              <span className="ed-pc-entry-id">{e.id}</span>
              <span className="ed-pc-entry-label">{e.label}</span>
              {e.rail && <span className="ed-pc-entry-rail" data-testid={`pc-rail-${e.id}`}>{e.rail}</span>}
              <span className="ed-pc-entry-amt">{e.amt}</span>
            </li>
          ))}
        </ul>
        <div className="ed-pc-net">
          <span className="ed-pc-net-label">{p.net}</span>
          <span className="ed-pc-net-amt">{p.netAmt}</span>
        </div>
        <div className="ed-pc-batch">
          <ArrowDown size={14} />
          <span className="ed-pc-batch-id">{p.batchId}</span>
          <ArrowDown size={14} />
          <span className="ed-pc-newtx">{p.newTx}</span>
          <ArrowDown size={14} />
          <span className="ed-pc-target">{p.target}</span>
        </div>
        <p className="ed-pc-legend">{p.legend}</p>
        {p.feeNote && (
          <div className="ed-pc-fee-note" data-testid="pc-fee-note">
            <ShieldCheck size={14} strokeWidth={2.4} />
            <span>{p.feeNote}</span>
          </div>
        )}
      </div>
    </section>
  );
};

const CommonRule = ({ t }) => {
  let counter = 0;
  return (
    <section className="ed-common-rule" data-testid="ed-common-rule">
      <div className="ed-common-rule-inner">
        <div className="ed-common-rule-tag">
          <ShieldCheck size={16} strokeWidth={2.4} />
          <span>{t.commonRule.tag}</span>
        </div>
        <p className="ed-common-rule-body">{t.commonRule.body}</p>
        <div className="ed-common-rule-sequence-tag">{t.commonRule.sequenceTag}</div>
        <div className="ed-common-rule-groups">
          {t.commonRule.sequenceGroups.map((group, gi) => (
            <div className="ed-common-rule-group" key={gi} data-testid={`rule-group-${gi}`}>
              <div className="ed-common-rule-group-tag">{group.tag}</div>
              <ol className="ed-common-rule-steps">
                {group.steps.map((step, si) => {
                  counter += 1;
                  return (
                    <li key={si} className="ed-common-rule-step">
                      <span className="ed-common-rule-step-num">{counter}</span>
                      <span className="ed-common-rule-step-text">{step}</span>
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   ROADMAP
   ============================================================ */
const Roadmap = ({ t }) => (
  <section id="roadmap" className="ed-section ed-roadmap" data-testid="ed-roadmap">
    <SectionHeader title={t.roadmap.title} subtitle={t.roadmap.subtitle} color="#bef264" />
    <div className="ed-roadmap-track">
      {t.roadmap.phases.map((p, i) => (
        <div
          key={i}
          className="ed-roadmap-step"
          style={{ animationDelay: `${i * 120}ms`, "--rc": p.color || "#bef264" }}
          data-testid={`roadmap-step-${i + 1}`}
        >
          <div className="ed-roadmap-num">{i + 1}</div>
          <div className="ed-roadmap-tag" data-testid={`roadmap-tag-${i + 1}`}>{p.tag}</div>
          <h3 className="ed-roadmap-title">{p.title}</h3>
          <p className="ed-roadmap-desc">{p.desc}</p>
          {p.mccs && p.mccs.length > 0 && (
            <div className="ed-roadmap-mccs" data-testid={`roadmap-mccs-${i + 1}`}>
              {p.mccs.map((code) => (
                <span key={code} className="ed-roadmap-mcc-chip">{code}</span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  </section>
);

/* ============================================================
   VERTICALS SECTION (tabs)
   ============================================================ */
const VerticalTabPanel = ({ t, lang, active, tab, data, color }) => {
  const [modelIdx, setModelIdx] = useState(0);
  const model = data.models[modelIdx];
  const TabIcon = TAB_ICONS[active];
  const flowSchema = FLOWS[model.code];
  const flowTitle = lang === "en" ? (FLOWS_EN_LABELS[model.code]?.title || flowSchema.title) : flowSchema.title;
  return (
    <div className="ed-tabpanel" data-testid={`tabpanel-${active}`}>
      <div className="ed-tabpanel-head">
        <div className="ed-tabpanel-icon" style={{ background: color }}>
          <TabIcon size={26} strokeWidth={2.2} />
        </div>
        <div>
          <h3 className="ed-tabpanel-title">{tab.long}</h3>
          <p className="ed-tabpanel-intro">{data.intro}</p>
        </div>
      </div>

      {/* MCC list — accepted Merchant Category Codes for this vertical */}
      {tab.mccs && tab.mccs.length > 0 && (
        <div className="ed-mcc-list" style={{ "--tc": color }} data-testid={`ed-mcc-list-${active}`}>
          <span className="ed-mcc-list-tag">{t.verticals.labels.mccList}</span>
          <div className="ed-mcc-list-chips">
            {tab.mccs.map((m) => (
              <span key={m.code} className="ed-mcc-list-chip" data-testid={`mcc-chip-${active}-${m.code}`}>
                <strong className="ed-mcc-list-chip-code">{m.code}</strong>
                <span className="ed-mcc-list-chip-label">{m.label}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Model chips — selection only, no explanatory text between chips and diagram */}
      <div className="ed-tab-sticky" style={{ "--tc": color }}>
        <div className="ed-model-chips" data-testid="ed-model-chips">
          {data.models.map((m, i) => (
            <button
              key={m.code}
              type="button"
              className={`ed-model-chip ${modelIdx === i ? "active" : ""}`}
              style={{ "--tc": color }}
              onClick={() => setModelIdx(i)}
              data-testid={`chip-${m.code}`}
            >
              <span className="ed-model-chip-code">{m.code}</span>
              <span className="ed-model-chip-title">{m.title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* UNIQUE FLOW DIAGRAM PER MODEL (3-layer vertical) — directly under chips */}
      <div className="ed-flow-wrap" style={{ "--tc": color }}>
        <div className="ed-flow-head">
          <span className="ed-flow-head-code">{model.code}</span>
          <span className="ed-flow-head-title">{flowTitle}</span>
        </div>
        <div className="ed-flow-rails">
          <span className="ed-flow-rail-tag ed-flow-rail-payer">
            {lang === "en" ? "Payer" : "Payeur"}
          </span>
          <span className="ed-flow-rail-tag ed-flow-rail-hub">
            {lang === "en" ? "EcoLunch · Wallet · Ledger" : "EcoLunch · Wallet · Ledger"}
          </span>
          <span className="ed-flow-rail-tag ed-flow-rail-bene">
            {lang === "en" ? "Final beneficiary" : "Bénéficiaire final"}
          </span>
        </div>
        <FlowDiagram schema={flowSchema} lang={lang} />
      </div>

      {/* FLOW · numbered explanatory steps — BELOW the diagram */}
      <div className="ed-flow-priority" data-testid="ed-flow-priority" style={{ "--tc": color }}>
        <div className="ed-flow-priority-head">
          <span className="ed-flow-priority-tag">{t.verticals.labels.flow}</span>
          <span className="ed-flow-priority-code" style={{ background: color }}>{model.code}</span>
          <span className="ed-flow-priority-title">{model.title}</span>
        </div>
        <ol className="ed-flow-priority-steps">
          {model.flow.split(/\s*→\s*/).map((step, idx) => (
            <li key={idx} className="ed-flow-priority-step">
              <span className="ed-flow-priority-step-num" style={{ background: color }}>{idx + 1}</span>
              <span className="ed-flow-priority-step-text">{step.trim()}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* MODEL DETAIL — roles table + principle only (flow moved above diagram) */}
      <div className="ed-model-detail" style={{ "--tc": color }}>
        <div className="ed-model-detail-head">
          <span className="ed-model-detail-code">{model.code}</span>
          <h4 className="ed-model-detail-title">{model.title}</h4>
        </div>
        <div className="ed-model-detail-grid">
          <div className="ed-roles-table">
            {Object.entries(model.roles).map(([k, v]) => (
              <div key={k} className="ed-roles-row">
                <span className="ed-roles-key">{t.verticals.labels[k]}</span>
                <strong className="ed-roles-val">{v}</strong>
              </div>
            ))}
          </div>
          <div className="ed-model-side">
            <div className="ed-principle-block">
              <div className="ed-principle-label">
                <ShieldCheck size={14} /> {t.verticals.labels.principle}
              </div>
              <p className="ed-principle-body">{model.principle}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const VerticalsSection = ({ t, lang }) => {
  const tabs = t.verticals.tabs;
  const [active, setActive] = useState(tabs[0].id);
  const tab = tabs.find((tt) => tt.id === active);
  const data = t.verticals[active];
  const color = V_COLORS[active];

  return (
    <section id="verticals" className="ed-section ed-verticals" data-testid="ed-verticals">
      <SectionHeader title={t.verticals.title} subtitle={t.verticals.subtitle} color="#a78bfa" />

      {/* TAB NAV — Lucide icons only, NO emojis */}
      <div className="ed-tabs" role="tablist" data-testid="ed-tabs">
        {tabs.map((tt) => {
          const Icon = TAB_ICONS[tt.id];
          return (
            <button
              key={tt.id}
              type="button"
              role="tab"
              aria-selected={active === tt.id}
              className={`ed-tab ${active === tt.id ? "active" : ""}`}
              style={{ "--tc": V_COLORS[tt.id] }}
              onClick={() => setActive(tt.id)}
              data-testid={`tab-${tt.id}`}
            >
              <span className="ed-tab-icon"><Icon size={18} strokeWidth={2.2} /></span>
              <span className="ed-tab-tag">{tt.tag}</span>
              {tt.mcc && (
                <span className="ed-tab-mcc" data-testid={`tab-mcc-${tt.id}`}>
                  MCC {tt.mcc}{tt.mccs && tt.mccs.length > 1 ? ` +${tt.mccs.length - 1}` : ""}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* TAB CONTENT */}
      <VerticalTabPanel key={active} t={t} lang={lang} active={active} tab={tab} data={data} color={color} />
    </section>
  );
};

/* ============================================================
   WALLET MODELS (W0..W9)
   ============================================================ */
const WALLET_ICONS = [Wallet, Receipt, Coins, CalendarCheck, Receipt, Repeat, Users, ShieldCheck, RefreshCw, CreditCard];

const WalletModels = ({ t }) => (
  <section id="wallet" className="ed-section ed-wallet" data-testid="ed-wallet">
    <SectionHeader title={t.walletModels.title} subtitle={t.walletModels.subtitle} color="#86efac" />
    <div className="ed-wallet-grid">
      {t.walletModels.rows.map((r, i) => {
        const Icon = WALLET_ICONS[i] || Wallet;
        return (
          <div key={r.code} className="ed-wallet-card" style={{ animationDelay: `${i * 50}ms` }}>
            <div className="ed-wallet-card-head">
              <div className="ed-wallet-card-icon"><Icon size={16} /></div>
              <span className="ed-wallet-code">{r.code}</span>
            </div>
            <h4 className="ed-wallet-title">{r.title}</h4>
            <p className="ed-wallet-desc">{r.desc}</p>
          </div>
        );
      })}
    </div>
  </section>
);

/* ============================================================
   FUNDING RAILS
   ============================================================ */
const Rails = ({ t }) => (
  <section className="ed-section ed-rails" data-testid="ed-rails">
    <SectionHeader title={t.rails.title} subtitle={t.rails.subtitle} color="#22d3ee" />
    <div className="ed-rails-chips">
      {t.rails.items.map((r, i) => (
        <span key={i} className="ed-rail-chip" style={{ animationDelay: `${i * 40}ms` }}>
          <Banknote size={12} />
          {r}
        </span>
      ))}
    </div>
  </section>
);

/* ============================================================
   LEDGER REQUIRED FIELDS
   ============================================================ */
const Ledger = ({ t }) => (
  <section id="ledger" className="ed-section ed-ledger" data-testid="ed-ledger">
    <SectionHeader title={t.ledger.title} subtitle={t.ledger.subtitle} color="#f472b6" />
    <div className="ed-ledger-block">
      <div className="ed-ledger-head">
        <Database size={14} />
        <span>ledger_required_fields.sql</span>
        <span className="ed-ledger-dots">
          <i /><i /><i />
        </span>
      </div>
      <ul className="ed-ledger-list">
        {t.ledger.fields.map((f, i) => (
          <li key={f} className="ed-ledger-field" style={{ animationDelay: `${i * 30}ms` }}>
            <span className="ed-ledger-num">{String(i + 1).padStart(2, "0")}</span>
            <code className="ed-ledger-name">{f}</code>
            <CheckCircle2 size={12} className="ed-ledger-check" />
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/* ============================================================
   WHY MoR
   ============================================================ */
const Why = ({ t }) => (
  <section id="why" className="ed-section ed-why" data-testid="ed-why">
    <SectionHeader title={t.why.title} subtitle={t.why.subtitle} color="#fb923c" />
    <div className="ed-why-grid">
      {t.why.points.map((p, i) => (
        <div key={i} className="ed-why-card" style={{ animationDelay: `${i * 100}ms` }}>
          <div className="ed-why-num">{String(i + 1).padStart(2, "0")}</div>
          <h3 className="ed-why-title">{p.title}</h3>
          <p className="ed-why-desc">{p.desc}</p>
        </div>
      ))}
    </div>
  </section>
);

/* ============================================================
   MAIN
   ============================================================ */
export default function EcoLunchDeck() {
  const [lang, setLang] = useState("fr");
  const t = I18N[lang];

  useEffect(() => {
    const handler = (e) => {
      const target = e.target.closest("a[href^='#']");
      if (!target) return;
      const href = target.getAttribute("href");
      if (!href || href === "#") return;
      const el = document.querySelector(href);
      if (!el) return;
      e.preventDefault();
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  return (
    <div className="ed-root" data-testid="ed-root">
      <div className="ed-bg-gradient" aria-hidden />
      <div className="ed-bg-grid" aria-hidden />
      <div className="ed-bg-glow ed-bg-glow-1" aria-hidden />
      <div className="ed-bg-glow ed-bg-glow-2" aria-hidden />
      <div className="ed-bg-glow ed-bg-glow-3" aria-hidden />

      <TopNav lang={lang} setLang={setLang} t={t} />
      <main>
        <Hero t={t} />
        <CommonRule t={t} />
        <Roadmap t={t} />
        <MegaMasterDiagram t={t} lang={lang} />
        <VerticalsSection t={t} lang={lang} />
        <PayoutCycle t={t} />
        <WalletModels t={t} />
        <Rails t={t} />
        <Ledger t={t} />
        <Why t={t} />
      </main>

      <footer className="ed-footer">{t.footer}</footer>
    </div>
  );
}
